import { formatEntriesCount, useLanguageNumberLocale } from "@/common/helpers/number-format";
import { cn } from "@/common/helpers/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TooltipVariant } from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";

interface DataTableItemsCountProps {
  count: number;
  isLoading?: boolean;
  className?: string;
}

export function DataTableItemsCount({ count, isLoading = false, className }: DataTableItemsCountProps) {
  const { t } = useTranslation("common");
  const numberFormatLocale = useLanguageNumberLocale();
  const formattedCount = formatEntriesCount(count, numberFormatLocale);
  const label = t("common:Entries", { count, formattedCount: formattedCount.display });

  if (isLoading) {
    return (
      <div
        className={cn(
          "border-border xs:flex-0 hidden h-9 flex-1 items-center rounded-lg border px-2 py-1 sm:flex sm:px-3 sm:py-0",
          className,
        )}
      >
        <Skeleton className="h-4 w-16" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "border-border xs:flex-0 hidden h-9 flex-1 items-center rounded-lg border px-2 py-1 sm:flex sm:px-3 sm:py-0",
        className,
      )}
    >
      {formattedCount.isCompact ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground text-sm whitespace-nowrap">{label}</span>
            </TooltipTrigger>
            <TooltipContent variant={TooltipVariant.OutlinedPrimary}>
              <p>{t("common:Entries", { count, formattedCount: formattedCount.full })}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <span className="text-muted-foreground text-sm whitespace-nowrap">{label}</span>
      )}
    </div>
  );
}
